/**
 * Input — MCK-20260521-003 Tier 1 primitive.
 *
 * The label is not optional and is not a placeholder: a placeholder disappears the moment
 * somebody types, which is exactly when a person coming back to a half-filled form needs
 * it. Geometry follows `design/project/screens/signin.jsx`.
 *
 * `error` renders the message through `InlineError` and wires `aria-invalid` +
 * `aria-describedby`, so the screen reader hears the same thing the eye sees.
 */

import type { InputHTMLAttributes, ReactNode } from 'react';
import { useId } from 'react';

import { InlineError } from './InlineError';
import styles from './Input.module.css';

/** `chrome` is the black half of the split entrance screen; `light` is everywhere else. */
export type InputTone = 'light' | 'chrome';

/** `md` is the form field; `sm` is the toolbar's — `Select`'s two, so a text field and a
    dropdown standing on one row of a filter panel are the same height (`KLQ-20260910-001`
    §C). TWIN of `SelectSize` in `Select.tsx`. */
export type InputSize = 'md' | 'sm';

export interface InputProps
  extends Omit<
    InputHTMLAttributes<HTMLInputElement>,
    // `size` is taken: on a native `<input>` it is *how many characters wide*, which is a
    // 1990s layout mechanism nothing here uses. This component's `size` is the field's
    // height, the way `Button`'s and `Select`'s are.
    'className' | 'id' | 'size'
  > {
  readonly label: string;
  /**
   * Draw the label as an accessible name only. For a control whose surroundings already say
   * what it is: a filter bar, a toolbar, a table cell. `Select`'s prop, verbatim.
   */
  readonly labelHidden?: boolean;
  readonly tone?: InputTone;
  readonly size?: InputSize;
  /** Rendered under the field, and announced. Its presence is what marks the field invalid. */
  readonly error?: string | undefined;
  /** Non-error helper text, replaced by `error` when there is one. */
  readonly hint?: ReactNode;
  /** Rendered inside the field's right edge — an icon button (`PasswordInput`'s eye), never
      decoration. The slot reserves its own padding so text never runs under it. */
  readonly trailing?: ReactNode;
}

export function Input({
  label,
  labelHidden = false,
  tone = 'light',
  size = 'md',
  error,
  hint,
  trailing,
  ...rest
}: InputProps) {
  const id = useId();
  const messageId = `${id}-message`;
  const invalid = error !== undefined && error !== '';

  const classes = [
    styles.field,
    styles[tone],
    styles[size],
    invalid ? styles.invalid : '',
    trailing !== undefined ? styles.hasTrailing : '',
  ]
    .filter(Boolean)
    .join(' ');

  return (
    <div className={classes}>
      <label className={labelHidden ? styles.srOnly : styles.label} htmlFor={id}>
        {label}
      </label>
      <div className={styles.inputWrap}>
        <input
          {...rest}
          id={id}
          className={styles.input}
          aria-invalid={invalid || undefined}
          aria-describedby={invalid || hint !== undefined ? messageId : undefined}
        />
        {trailing !== undefined ? <div className={styles.trailing}>{trailing}</div> : null}
      </div>
      {invalid ? (
        <InlineError id={messageId}>{error}</InlineError>
      ) : hint !== undefined ? (
        <p className={styles.hint} id={messageId}>
          {hint}
        </p>
      ) : null}
    </div>
  );
}
